<template>
  <div class="grid room-card-horizontal">
    <div class="relative h-full">
      <div
        class="c-room-card__heart"
        :class="{'c-room-card__heart--selected': isFavourite}"
        @click="toggleFavourite(room.room_id)"
      />

      <RoomNativeCarousel :room="room" :index="-1" @beforeSlide="beforeSlide" />

      <div class="c-room-card__price c-room-card__price--htmlBottom">
        from <span class="c-room-card__price_val">{{ room.room_price }}</span><br>
        {{ room.room_price_method }} {{ room.room_price_type ? ' / ' + room.room_price_type : '' }}
      </div>
    </div>

    <div class="room-card-horizontal__content overflow-hidden py-3 pr-3 pl-4 border border-l-0 border-solid border-grey-300 rounded-r-lg relative">
      <div class="mt-1 mb-2 text-xl font-semibold flex items-center h-7 flex-start">
        <div v-if="room.is_supervenue" class="w-5 h-full flex items-center mr-1">
          <img width="16" height="22.4" alt="Supervenue" :src="'/build/images/supervenue.svg'">
        </div>

        <a
          class="truncate inline-block text-grey-800 hover:no-underline hover:text-grey-800 visited:text-grey-800 visited:no-underline focus:text-grey-800 focus:no-underline"
          :href="room.room_url"
          target="_blank"
          @click="emit('trackClick')"
        >
          {{ room.room_name }}
        </a>

        <div v-if="room.room_reviews_count > 0" class="flex items-center text-sm font-semibold ml-auto">
          <RatingStars class="mr-1 items-center" size="sm" :score="room.room_rating" />
          ({{ room.room_reviews_count }})
        </div>
      </div>

      <div class="mb-2 truncate">
        {{ room.venue_name }}
      </div>

      <div class="mb-2 text-grey-600 flex">
        {{ room.space_type_label }}
        <span v-if="room.venue_part_short_label && room.space_type_label" class="mx-2">·</span>
        {{ room.venue_part_short_label }}
      </div>

      <div class="mb-2 flex item-center">
        <div v-if="room.room_seating_capacity > 0" class="flex items-center whitespace-nowrap">
          <SeatingIcon class="w-4 h-4 mr-1" />{{ room.room_seating_capacity }} seats
        </div>

        <span v-if="room.room_seating_capacity > 0 && room.room_standing_capacity > 0" class="mx-2">·</span>

        <div v-if="room.room_standing_capacity > 0" class="flex items-center whitespace-nowrap">
          <StandingIcon class="w-4 h-4 mr-0.5" />{{ room.room_standing_capacity }} standing
        </div>
      </div>

      <div class="mb-2 flex items-center my-1">
        <div v-if="room.metro_station" class="flex items-center whitespace-nowrap">
          <TrainIcon class="w-4 h-4 mr-1" />
          {{ room.metro_station }}
        </div>

        <span v-if="room.metro_station && room.geo_project_location" class="mx-2">·</span>

        <div class="flex items-center whitespace-nowrap">
          <PinIcon class="w-4 h-4 mr-1" />
          {{ room.geo_project_location }}
        </div>
      </div>

      <div v-if="responseTimeFormatted || props.managerLastActivityOnline !== null && props.managerLastActivityOnline < 15" class="mb-2 flex items-center">
        <div
          v-if="props.managerLastActivityOnline !== null && props.managerLastActivityOnline < 15"
          class="flex items-center h-5"
        >
          <ActiveTimeAgoLabel :value="props.managerLastActivityOnline" />
        </div>

        <div
          v-else
          class="flex items-center whitespace-nowrap"
        >
          <ClockIcon class="w-4 h-4 mr-1" />
          Responds within {{ responseTimeFormatted }}
        </div>
      </div>

      <div v-if="featuresList.length" class="border-0 border-t border-solid border-grey-200 mt-4 pt-4">
        <div class="flex items-center overflow-hidden h-5 flex-wrap">
          <div
            v-for="(feature, index) in featuresList"
            class="flex items-center whitespace-nowrap"
            :class="{'text-grey-500': !feature.available}"
            :key="feature.id"
          >
            <div v-if="index !== 0 && index !== featuresList.length" class="mx-2">
              ·
            </div>

            <component :is="feature.icon" v-if="feature.icon" class="w-4 h-4 mr-2" />
            <CheckIcon v-else-if="feature.available" class="w-4 h-4 mr-1" />
            <XIcon v-else class="w-4 h-4 mr-1" />

            <span :class="{'line-through': !feature.available}">
              {{ feature.label }}
              <span v-if="feature.id === 1 && room.alcohol_license_time_limit_formatted" class="font-semibold">
                until {{ room.alcohol_license_time_limit_formatted }}
              </span>
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
// icons
import SeatingIcon from '@tagvenue/icons/24/bold/education/sitting-chair.svg';
import CheckIcon from '@tagvenue/icons/24/bold/general/check.svg';
import XIcon from '@tagvenue/icons/24/bold/general/x.svg';
import StandingIcon from '@tagvenue/icons/24/bold/users/user-standing.svg';
import FlipchartIcon from '@tagvenue/icons/24/line/charts/presentation-chart-03.svg';
import PhoneIcon from '@tagvenue/icons/24/line/communication/phone.svg';
import ProjectorIcon from '@tagvenue/icons/24/line/finance-ecommerce/safe.svg';
import GlassIcon from '@tagvenue/icons/24/line/general/glass-01.svg';
import PinIcon from '@tagvenue/icons/24/line/maps-travel/marker-pin-01.svg';
import TrainIcon from '@tagvenue/icons/24/line/maps-travel/train.svg';
import MusicIcon from '@tagvenue/icons/24/line/media-devices/music-note-01.svg';
import SpeakerIcon from '@tagvenue/icons/24/line/media-devices/speaker-01.svg';
import TvIcon from '@tagvenue/icons/24/line/media-devices/tv-03.svg';
import WifiIcon from '@tagvenue/icons/24/line/media-devices/wifi.svg';
import ClockIcon from '@tagvenue/icons/24/line/time/clock.svg';
import SunIcon from '@tagvenue/icons/24/line/weather/sun.svg';
import AirConditionerIcon from '@tagvenue/icons/24/line/weather/wind-02.svg';
import { onMounted, ref } from 'vue';
import { useStore } from 'vuex';

import ActiveTimeAgoLabel from '@/components/Ui/ActiveTimeAgoLabel.vue';
import RatingStars from '@/components/Ui/Rating/RatingStars.vue';
import { formatResponseTime } from '@/services/response-time-formatter';

import RoomNativeCarousel from './RoomNativeCarousel.vue';

const store = useStore();
const { highlightedRoomData: room } = store.state;
const emit = defineEmits(['trackClick', 'beforeSlide']);

const props = defineProps({
  managerLastActivityOnline: {
    type: Number,
    default: () => null,
  },
  forceFeaturesVisibilityIds: {
    type: Array,
    default: () => [],
  },
});

const featuresConfig = {
  1: {
    label: 'Alcohol licence',
    icon: GlassIcon,
  },
  2: {
    label: 'Alcohol licence extension',
    icon: null,
  },
  3: {
    label: 'Wedding licence available',
    icon: null,
  },
  13: {
    label: 'Conference call facilities',
    icon: PhoneIcon,
  },
  17: {
    label: 'Play your own music',
    icon: MusicIcon,
  },
  5: {
    label: 'Wi-Fi available',
    icon: WifiIcon,
  },
  12: {
    label: 'PA system / music speakers',
    icon: SpeakerIcon,
  },
  8: {
    label: 'Projector',
    icon: ProjectorIcon,
  },
  7: {
    label: 'Air conditioner',
    icon: AirConditionerIcon,
  },
  9: {
    label: 'Flatscreen TV',
    icon: TvIcon,
  },
  11: {
    label: 'Flipchart',
    icon: FlipchartIcon,
  },
  6: {
    label: 'Natural light',
    icon: SunIcon,
  },
  16: {
    label: 'Storage space',
    icon: null,
  },
};
const featuresIds = room.room_features_ids || [];
const isFeatureAvailable = (id, roomData) => {
  if (id === 1) {
    return roomData?.alcohol_license_time_limit_formatted;
  }
  if (id === 2) {
    return roomData?.alcohol_license_extension;
  }
  return roomData?.room_features_ids?.includes(id);
};
let featuresList = [];
if (props.forceFeaturesVisibilityIds.length) {
  featuresList = props.forceFeaturesVisibilityIds.filter(id => featuresConfig[id]).map(id => {
    return {
      id,
      available: isFeatureAvailable(id, room),
      icon: featuresConfig[id]?.icon,
      label: featuresConfig[id]?.label,
    };
  }).sort((a, b) => b.available - a.available);
} else {
  featuresList = featuresIds.filter(id => featuresConfig[parseInt(id, 10)]).map(id => {
    return {
      id,
      available: true,
      icon: featuresConfig[id]?.icon,
      label: featuresConfig[id]?.label,
    };
  });
}

const isFavourite = ref(false);
const responseTimeFormatted = formatResponseTime(room.response_time);

const toggleFavourite = (id) => {
  App.es6.shared.wish_list.then(async (api) => {
    const saved = await api.toggleSaved(id);
    if (saved) {
      isFavourite.value = !isFavourite.value;
    }
  });
};

const beforeSlide = (e) => {
  emit('beforeSlide', e);
};

// hook on mounted
onMounted(() => {
  window.App.es6.shared.wish_list.then(api => {
    isFavourite.value = api.isFavourite(room.room_id);
  });
});
</script>

<style scoped lang="scss">
.room-card-horizontal {grid-template-columns: 379px 1fr;}
.room-card-horizontal__content {height: 252px;}
.room-card-horizontal__content a:after {content: ''; position: absolute; top: 0; left: 0; right: 0; bottom: 0;}

.room-card-horizontal :deep(.lory-slider) {height: 100%;}
.room-card-horizontal :deep(.lory-slider a) {border-radius: 8px 0 0 8px;}
</style>
